import type { UseQueryResult } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { useDropUnknownCarrier } from './useDropUnknownCarrier';
import { useSignupNavigation } from './useSignupNavigation';
import type { SignupSponsor } from '../services/signup-api/signup-api';
import { linkNamesSponsor } from '../services/sponsor-lookup';

/**
 * Sends the member to the error screen, once, when the employer a link names
 * could not be looked up, whatever the reason. Production never continues an
 * employer link without the employer's config, so a screen that acts on the
 * sponsor must not quietly carry on as if there were none. Returns whether the
 * lookup failed, so the caller can hold back anything that needs the sponsor.
 * Only the employer-link path leaves: on a search path (an Express Scripts
 * link included) the sponsor is the pick, which may be a decoy that must
 * behave like any other entry. An Express Scripts link whose carrier the
 * portal does not know goes back to the carrier search instead.
 */
export function useLeaveOnSponsorFailure(
  sponsor: UseQueryResult<SignupSponsor>,
  clientId: string | undefined,
): boolean {
  const { goToErrorScreen, path } = useSignupNavigation();
  const failed =
    path === 'employer-link' &&
    linkNamesSponsor(clientId) &&
    sponsor.isError &&
    sponsor.data === undefined;
  const dropping = useDropUnknownCarrier(sponsor, 'later_step', failed);
  const left = useRef(false);
  useEffect(() => {
    if (!failed || dropping || left.current) return;
    left.current = true;
    goToErrorScreen();
  }, [failed, dropping, goToErrorScreen]);
  return failed;
}
